import { UseQueryResult, useQuery } from "@tanstack/react-query";

import { ShowWithComedian } from "../shows";

type ShowsQueryParams = {
  postalCode?: string | null;
  countryCode?: string | null;
  radius?: number | null;
  limitDays?: number | null;
  venuePageId?: string | null;
  comedianId?: string | null;
  followedComedians?: boolean | null;
};

async function fetchShows(
  params: ShowsQueryParams
): Promise<ShowWithComedian[]> {
  const searchParams = new URLSearchParams();

  // Add parameters to query string
  if (params.postalCode) searchParams.append("postalCode", params.postalCode);
  if (params.countryCode)
    searchParams.append("countryCode", params.countryCode);
  if (params.radius) searchParams.append("radius", params.radius.toString());
  if (params.limitDays)
    searchParams.append("limitDays", params.limitDays.toString());
  if (params.venuePageId)
    searchParams.append("venuePageId", params.venuePageId);
  if (params.comedianId) searchParams.append("comedianId", params.comedianId);
  if (
    params.followedComedians !== null &&
    params.followedComedians !== undefined
  )
    searchParams.append(
      "followedComedians",
      params.followedComedians.toString()
    );

  const response = await fetch(`/api/shows?${searchParams.toString()}`);

  if (!response.ok) {
    throw new Error(`Error fetching shows: ${response.statusText}`);
  }

  return response.json();
}

export function useShowsQuery(
  params: ShowsQueryParams,
  options = {}
): UseQueryResult<ShowWithComedian[], Error> {
  // Create a stable query key from params
  const queryKey = ["shows", params];

  return useQuery({
    queryKey,
    queryFn: () => fetchShows(params),
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
    ...options,
  });
}

// Create a specialized hook for nearby shows
export function useNearbyShowsQuery(
  postalCode: string | null,
  countryCode: string | null,
  radius: number | null,
  filterState: {
    dateFilter?: string;
    myComedians?: boolean;
    punchupVenues?: boolean;
  } = {},
  options = {}
) {
  const params: ShowsQueryParams = {
    postalCode,
    countryCode,
    radius,
    followedComedians: filterState.myComedians || null,
    // Can add more specialized params based on filter state
  };

  // Add any date limiting logic based on dateFilter
  if (filterState.dateFilter === "thisWeek") {
    params.limitDays = 7;
  } else if (filterState.dateFilter === "thisWeekend") {
    params.limitDays = 3; // Approximate
  }

  return useShowsQuery(params, options);
}
